import { DialogHeader, DialogTitle } from "~/components/ui/dialog"
import { LinkIcon, X } from "lucide-react"
import {
    FacebookShareButton,
    FacebookIcon,
    PinterestShareButton,
    PinterestIcon,
    LinkedinShareButton,
    LinkedinIcon,
    WhatsappShareButton,
    WhatsappIcon,
    EmailShareButton,
    EmailIcon,
    TwitterShareButton,
    TwitterIcon,
    FacebookMessengerShareButton,
    FacebookMessengerIcon,
} from "next-share"
import { useState } from "react"
import { DialogClose, DialogContent } from "@radix-ui/react-dialog"
import { Separator } from "~/components/ui/separator"

interface ShareDialogContentProps {
    url: string
    title: string
    media?: string // For Pinterest
    toggleShareOpen?: VoidFunction
}

export function ShareDialogContent({ url, title = "Share this Property", media, toggleShareOpen }: ShareDialogContentProps) {
    const [copied, setCopied] = useState(false)

    const handleCopyLink = () => {
        navigator.clipboard
            .writeText(url)
            .then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            })
            .catch((err) => {
                console.error("Could not copy text: ", err)
            })
    }

    return (
        <>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className='text-base font-medium md:text-2xl flex justify-between items-center w-full px-4 md:px-0 md:font-semibold py-3 pb-0 text-oh-black'>
                        {title}
                    </DialogTitle>
                </DialogHeader>
            </DialogContent>
            <div className="px-4">
                <Separator className='w-full border-t border-gray-200 ' />
                <div className='grid grid-cols-4 gap-6 py-4'>
                    <div className='flex flex-col items-center gap-2'>
                        <FacebookShareButton url={url} quote={title} aria-label="Share on Facebook">
                            <FacebookIcon size={48} round />
                        </FacebookShareButton>
                        <span className='text-xs font-medium'>Facebook</span>
                    </div>

                    <div className='flex flex-col items-center gap-2'>
                        <FacebookMessengerShareButton url={url} appId='your-app-id' aria-label="Share on Facebook Messenger">
                            <FacebookMessengerIcon size={48} round />
                        </FacebookMessengerShareButton>
                        <span className='text-xs font-medium'>Messenger</span>
                    </div>

                    <div className='flex flex-col items-center gap-2'>
                        <LinkedinShareButton url={url} aria-label="Share on LinkedIn">
                            <LinkedinIcon size={48} round />
                        </LinkedinShareButton>
                        <span className='text-xs font-medium'>LinkedIn</span>
                    </div>

                    <div className='flex flex-col items-center gap-2'>
                        <TwitterShareButton url={url} title={title} aria-label="Share on X (Twitter)">
                            <TwitterIcon size={48} round />
                        </TwitterShareButton>
                        <span className='text-xs font-medium'>X</span>
                    </div>

                    <div className='flex flex-col items-center gap-2'>
                        <PinterestShareButton
                            url={url}
                            media={media || url}
                            description={title}
                            aria-label="Share on Pinterest"
                        >
                            <PinterestIcon size={48} round />
                        </PinterestShareButton>
                        <span className='text-xs font-medium'>Pinterest</span>
                    </div>

                    <div className='flex flex-col items-center gap-2'>
                        <WhatsappShareButton url={url} title={title} separator=' ' aria-label="Share on WhatsApp">
                            <WhatsappIcon size={48} round />
                        </WhatsappShareButton>
                        <span className='text-xs font-medium'>WhatsApp</span>
                    </div>

                    <div className='flex flex-col items-center gap-2'>
                        <EmailShareButton
                            url={url}
                            subject={title}
                            body={`Check this out: ${url}`}
                            aria-label="Share via Email"
                        >
                            <EmailIcon size={48} round />
                        </EmailShareButton>
                        <span className='text-xs font-medium'>Email</span>
                    </div>

                    <div className='flex flex-col items-center gap-2'>
                        <button
                            className='flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300'
                            onClick={handleCopyLink}
                            aria-label={copied ? "Link copied" : "Copy link to clipboard"}
                        >
                            <LinkIcon className='h-5 w-5' />
                        </button>
                        <span className='text-xs font-medium'>
                            {copied ? "Copied!" : "Copy Link"}
                        </span>
                    </div>
                </div>
            </div>

        </>
    );
}

